import type { Quality } from './post';

/**
 * Pixel ratio of the game canvas (owner: render). Pure and DOM-free, so the policy is unit-tested
 * (resolution.test.ts) and the render system only feeds it the frame time.
 *
 * - Desktop: a fixed ratio per tier (HIGH up to 1.5, MEDIUM 1.0, LOW 0.75). MEDIUM is what AUTO drops a
 *   slow machine to, so it must not render more pixels than it did before (the old post stack built
 *   MEDIUM's targets at 1.0 whatever the canvas ratio was).
 * - Touch (phones, tablets, docs/MOBILE.md): LOW pins 1.0. AUTO at MEDIUM steps 1.0 → 1.25 → 1.5 on the
 *   measured frame time, with hysteresis: up only after a long run at the display rate, down after a
 *   short slow stretch, and a level that proved too slow is not tried again for a while. A hand-picked
 *   MEDIUM stays at 1.0 and HIGH at 1.5.
 * - Always: never above the device pixel ratio, and the drawing buffer stays inside a pixel budget so a
 *   big tablet (or a 4K desktop) cannot allocate render targets the browser kills the tab for.
 */

/** Touch AUTO steps (MEDIUM tier). */
export const TOUCH_STEPS = [1, 1.25, 1.5] as const;
/** Drawing-buffer budget (device pixels). Touch: ~1.2 MP keeps MEDIUM's render targets near 50 MB. */
export const TOUCH_MAX_PIXELS = 1.2e6;
/** Desktop: about 4K, i.e. no change for any normal desktop window. */
export const DESKTOP_MAX_PIXELS = 8.3e6;

export interface RatioInput {
  tier: Quality;
  /** The graphics setting: 'auto' adapts, a tier is a hand pick. */
  auto: boolean;
  touch: boolean;
  dpr: number;
  /** CSS size of the canvas. */
  cssW: number;
  cssH: number;
  /** Touch AUTO step (index into TOUCH_STEPS). */
  step: number;
}

/** Highest ratio whose drawing buffer (cssW × cssH × r²) fits `maxPixels`. */
export function budgetRatio(cssW: number, cssH: number, maxPixels: number): number {
  const area = Math.max(1, cssW) * Math.max(1, cssH);
  return Math.sqrt(maxPixels / area);
}

/** The pixel ratio for a tier / setting / device. */
export function pixelRatioFor(i: RatioInput): number {
  const dpr = Math.max(0.5, i.dpr || 1);
  let r: number;
  if (i.touch) {
    if (i.tier === 'low') r = 1;
    else if (i.tier === 'high') r = 1.5;
    else r = i.auto ? TOUCH_STEPS[Math.max(0, Math.min(TOUCH_STEPS.length - 1, i.step))]! : 1;
  } else {
    r = i.tier === 'low' ? 0.75 : i.tier === 'medium' ? 1 : 1.5;
  }
  r = Math.min(r, dpr);
  // LOW on desktop deliberately renders below 1:1 (the 0.75 above); everything else is capped by the budget.
  const cap = budgetRatio(i.cssW, i.cssH, i.touch ? TOUCH_MAX_PIXELS : DESKTOP_MAX_PIXELS);
  r = Math.min(r, Math.max(cap, i.touch ? 1 : 0.75));
  return Math.round(r * 100) / 100;
}

/** Highest touch step whose ratio still fits the device (dpr) and the pixel budget. */
export function maxTouchStep(dpr: number, cssW: number, cssH: number): number {
  const cap = Math.min(dpr, budgetRatio(cssW, cssH, TOUCH_MAX_PIXELS));
  let s = 0;
  for (let k = 1; k < TOUCH_STEPS.length; k++) if (TOUCH_STEPS[k]! <= cap + 1e-6) s = k;
  return s;
}

/**
 * Frame-time driven step for touch AUTO. `update(dt)` returns +1 / -1 when the step should move.
 * - Down: 1.5 s below 50 fps (the tier drop in the render system waits for 3 s below 45 fps, so the
 *   pixel ratio goes first).
 * - Up: 6 s at 57+ fps; a step that was dropped is barred for 30 s, and for 120 s once it failed twice.
 * - Frames longer than 0.25 s (a tab switch, a shader compile) are ignored.
 */
export class PixelStepGovernor {
  step = 0;
  maxStep = TOUCH_STEPS.length - 1;
  private slowFor = 0;
  private fastFor = 0;
  private readonly barredUntil = new Float64Array(TOUCH_STEPS.length);
  private readonly fails = new Int32Array(TOUCH_STEPS.length);
  private clock = 0;

  /** Forget timers (after a tier change, a context restore or a resize). */
  settle(): void {
    this.slowFor = 0;
    this.fastFor = 0;
  }

  update(dt: number): -1 | 0 | 1 {
    if (!(dt > 0) || dt > 0.25) return 0;
    this.clock += dt;
    const fps = 1 / dt;
    if (fps < 50) {
      this.slowFor += dt;
      this.fastFor = 0;
    } else if (fps >= 57) {
      this.fastFor += dt;
      this.slowFor = Math.max(0, this.slowFor - dt);
    } else {
      this.slowFor = Math.max(0, this.slowFor - dt * 0.5);
      this.fastFor = Math.max(0, this.fastFor - dt);
    }
    if (this.step > this.maxStep) {
      this.step = this.maxStep;
      this.settle();
      return -1;
    }
    if (this.slowFor > 1.5 && this.step > 0) {
      const failed = this.step;
      this.fails[failed]!++;
      this.barredUntil[failed] = this.clock + (this.fails[failed]! >= 2 ? 120 : 30);
      this.step--;
      this.settle();
      return -1;
    }
    if (this.fastFor > 6 && this.step < this.maxStep && this.clock >= this.barredUntil[this.step + 1]!) {
      this.step++;
      this.settle();
      return 1;
    }
    return 0;
  }
}
